import { useEffect, useRef, useState } from 'preact/hooks';
import { listSessions, renameSession, deleteSession, describeApiError } from '../lib/api';
import { active, activeToken, activeDbId } from '../store/global';
import type { SessionSummary } from '../types/agno-api';
import { PageHeader } from '../components/PageHeader';
import { DataTable } from '../components/DataTable';
import { UserFilter } from './Memories';
import { formatRelative, truncate } from '../lib/format';

export function Sessions() {
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Contador de requisição: só a última `refresh()` escreve no estado. Sem isso
  // uma chamada anterior (ex: disparada antes do `activeDbId` chegar) podia
  // voltar 400 DEPOIS da boa e sobrescrever a lista com [] + erro.
  const reqSeq = useRef(0);

  const refresh = async () => {
    if (!activeToken.value) return;
    const seq = ++reqSeq.current;
    const stale = () => seq !== reqSeq.current;
    setLoading(true);
    setError(null);
    try {
      // Escopo = alvo ativo (type + component_id), como o histórico do chat.
      // Filtro de user_id explícito acima.
      const ref = active.value;
      const params: Parameters<typeof listSessions>[0] = {};
      if (ref) {
        params.type = ref.isTeam ? 'team' : 'agent';
        params.component_id = ref.targetId;
      }
      if (userFilter.trim()) params.user_id = userFilter.trim();
      const res = await listSessions(params);
      if (stale()) return;
      const raw = Array.isArray(res) ? res : (res as { data: SessionSummary[] }).data || [];
      // Agno 3.x lista com `session_id` (sem `id`). Sem normalizar, rowKey,
      // renomear e excluir usavam `undefined` → `/sessions/undefined`.
      const data = raw
        .map((s) => ({ ...s, id: s.id ?? s.session_id ?? '' }))
        .filter((s) => s.id);
      setSessions(data);
    } catch (e) {
      // Antes só ia pro console — um 400 de db_id ausente parecia "nenhuma sessão".
      if (stale()) return;
      console.error(e);
      setSessions([]);
      setError(describeApiError(e));
    } finally {
      if (!stale()) setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // activeDbId: trocar de alvo pode trocar o DB de sessões do AgentOS.
  }, [activeToken.value, activeDbId.value, active.value?.targetId, active.value?.isTeam, userFilter]);

  const rename = async (id: string) => {
    if (!draft.trim()) return;
    try {
      await renameSession(id, draft.trim());
      setEditing(null);
      refresh();
    } catch (e) {
      alert(`Falha ao renomear: ${describeApiError(e)}`);
    }
  };

  const remove = async (id: string) => {
    if (!confirm('Excluir esta sessão? Esta ação não pode ser desfeita.')) return;
    try {
      await deleteSession(id);
      refresh();
    } catch (e) {
      alert(`Não foi possível excluir a sessão: ${describeApiError(e)}`);
    }
  };

  return (
    <div class="p-6 max-w-6xl">
      <PageHeader
        title="Sessões"
        description={`Conversas do alvo selecionado${
          active.value ? ` (${active.value.targetId})` : ''
        }, agrupadas por session_id. Você pode renomear ou excluir.${
          activeDbId.value ? ` DB: ${activeDbId.value}.` : ''
        }`}
        icon={<i class="ph ph-clock-counter-clockwise text-xl" />}
        actions={
          <button
            class="inline-flex items-center justify-center gap-2 h-8 px-4 rounded-md text-sm font-medium bg-background text-foreground border border-input shadow-xs hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:border-ring disabled:opacity-50 disabled:pointer-events-none"
            onClick={refresh}
            disabled={loading}
          >
            {loading ? 'Atualizando…' : 'Atualizar'}
          </button>
        }
      />

      <UserFilter value={userFilter} onChange={setUserFilter} />

      {error && (
        <div class="mb-3 text-xs text-destructive font-mono border-l-2 border-destructive pl-2 break-words">
          Falha ao listar sessões: {error}
        </div>
      )}

      <DataTable
        rows={sessions as unknown as Array<Record<string, unknown>>}
        rowKey={(r) => (r as unknown as SessionSummary).id}
        columns={[
          {
            key: 'name',
            label: 'Sessão',
            render: (r) => {
              const s = r as unknown as SessionSummary;
              const id = s.id;
              if (editing === id) {
                return (
                  <div class="flex items-center gap-1">
                    <input
                      class="h-8 w-64 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:border-ring disabled:opacity-50"
                      value={draft}
                      onInput={(e) => setDraft((e.target as HTMLInputElement).value)}
                      autoFocus
                    />
                    <button
                      class="inline-flex items-center justify-center size-8 rounded-md text-success hover:bg-accent focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
                      onClick={() => rename(id)}
                      title="Salvar"
                      aria-label="Salvar"
                    >
                      <i class="ph ph-check text-sm" />
                    </button>
                    <button
                      class="inline-flex items-center justify-center size-8 rounded-md hover:bg-accent focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
                      onClick={() => setEditing(null)}
                      title="Cancelar"
                      aria-label="Cancelar"
                    >
                      <i class="ph ph-x text-sm" />
                    </button>
                  </div>
                );
              }
              return (
                <div>
                  <div class="font-medium">{s.session_name || truncate(s.id, 40)}</div>
                  <div class="text-[11px] text-muted-foreground font-mono">{s.id}</div>
                </div>
              );
            },
          },
          {
            key: 'user',
            label: 'User ID',
            width: '30ch',
            render: (r) => {
              const uid = (r as unknown as SessionSummary).user_id;
              if (!uid) return <span class="text-xs text-muted-foreground">—</span>;
              return (
                <button
                  class="font-mono text-xs text-left break-all text-muted-foreground hover:text-foreground hover:underline"
                  onClick={() => setUserFilter(uid)}
                  title="Filtrar por este user_id"
                >
                  {uid}
                </button>
              );
            },
          },
          { key: 'updated', label: 'Atualizado', render: (r) => <span class="text-xs">{formatRelative((r as unknown as SessionSummary).updated_at)}</span>, width: '16ch' },
          {
            key: 'actions',
            label: '',
            width: '8ch',
            render: (r) => {
              const s = r as unknown as SessionSummary;
              return (
                <div class="flex items-center gap-1">
                  <button
                    class="inline-flex items-center justify-center size-8 rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
                    onClick={() => {
                      setEditing(s.id);
                      setDraft(s.session_name || '');
                    }}
                    title="Renomear"
                    aria-label="Renomear"
                  >
                    <i class="ph ph-pencil-simple text-sm" />
                  </button>
                  <button
                    class="inline-flex items-center justify-center size-8 rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-destructive/40"
                    onClick={() => remove(s.id)}
                    title="Excluir"
                    aria-label="Excluir"
                  >
                    <i class="ph ph-trash text-sm" />
                  </button>
                </div>
              );
            },
          },
        ]}
        empty={
          activeToken.value
            ? userFilter
              ? `Nenhuma sessão para o user_id "${userFilter}". Inicie uma conversa no chat para começar.`
              : 'Nenhuma sessão ainda. Inicie uma conversa no chat para começar.'
            : 'Configure o token nas configurações para ver as sessões.'
        }
      />
    </div>
  );
}
